import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { SINK_TYPE } from '@shared-lib/sink-types';
import { clearTypeDefaultRegistry } from './typeDefaultRegistry';
import { pickDefaultForType } from './glbModelHelpers';

// Cache for GLB models by category (resolved results)
const glbModelCache = new Map();
// In-flight promises so concurrent callers for the same category share one fetch
const glbModelInflight = new Map();

// Resolved `type -> default model` index, built from ONE query for the whole
// catalog's defaults. Module-scoped promise so concurrent callers dedupe and
// later callers get the map without hitting Firestore again.
let typeDefaultIndexPromise = null;

/**
 * Build the per-type default index with a single
 * `isDefaultForType == true` read (~20 docs) instead of one
 * `type == <type>` read per type (which returns EVERY model of that type —
 * 77 wall-lights, 62 sinks — to pick one).
 *
 * Ordering note: both that query and the old per-type query are single-equality
 * queries, so Firestore returns each in `__name__` order. The first
 * non-disabled default of a type is therefore the same document either way,
 * and `pickDefaultForType`'s "first match wins" semantics are preserved.
 *
 * Keyed by BOTH `type` and the legacy `category` field, because the bucket
 * path this replaces queries both (`getGLBModelsByCategory` retries on
 * `category` when `type` returns nothing). Indexing only `type` meant a miss
 * could not be trusted, so every type with no flagged default fell through to
 * a full bucket read — two round trips, both of which can only ever return
 * documents this one query has already seen. With both keys indexed a miss in
 * both maps is PROOF that no default exists, and the fallback can be skipped.
 *
 * That is not a micro-optimisation: `useTypeDefaultGlbModels` resolves every
 * type in the scan before any object knows its model id, so each of those
 * wasted bucket reads sat directly in front of the GLB downloads. The types
 * that pay it are exactly the ones that log "No default GLB model set".
 */
const loadTypeDefaultIndex = () => {
  if (!typeDefaultIndexPromise) {
    typeDefaultIndexPromise = getDocs(
      query(
        collection(db, 'unifiedObjects'),
        where('isDefaultForType', '==', true)
      )
    )
      .then((snapshot) => {
        const byType = new Map();
        const byCategory = new Map();
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Mirrors the `disabled !== true` filter the per-category path
          // applies BEFORE picking the default.
          if (data.disabled === true) return;
          const model = { ...data, id: docSnap.id };
          if (data.type && !byType.has(data.type)) {
            byType.set(data.type, model);
          }
          // `category` is a plain string on catalog documents (it is the room
          // object's `category` that is a map). Guard the type so a stray map
          // can never become a key.
          if (
            typeof data.category === 'string' &&
            data.category &&
            !byCategory.has(data.category)
          ) {
            byCategory.set(data.category, model);
          }
        });
        return { byType, byCategory };
      })
      .catch((err) => {
        // Drop the failed promise so the next call retries.
        typeDefaultIndexPromise = null;
        throw err;
      });
  }
  return typeDefaultIndexPromise;
};

/**
 * Get GLB models for a specific object category
 * @param {string} category - The object category (e.g., 'toilet', 'sink', 'bathtub', 'shower')
 * @param {object} options - Optional filtering options
 * @param {number} options.wallCount - For showers: filter by wallCount (1, 2, or 3)
 * @returns {Promise<Array>} Array of GLB models for the category
 */
/**
 * Fetch the full catalog for a multi-type category: query every
 * Firestore `type` the category covers (a UI key like `tub-faucet` is a
 * grouping, not a stored type) and de-dup by id. Shared by the Design
 * Center's See All and the Design Packages manage page.
 */
export const getGLBModelsForTypes = async (types = [], options = {}) => {
  const perType = await Promise.all(
    types.map((t) => getGLBModelsByCategory(t, options))
  );
  const seen = new Set();
  return perType.flat().filter((m) => {
    if (!m?.id || seen.has(m.id)) return false;
    seen.add(m.id);
    return true;
  });
};

export const getGLBModelsByCategory = async (category, options = {}) => {
  if (!category || !db) {
    return [];
  }

  // Create cache key including wallCount for showers, sinkType for sinks, and
  // any generic subtype narrowing (pendants inside `ceiling-light`). A cache
  // key that ignored the narrowing would serve the whole type to the next
  // caller that asked for one slice of it, and vice versa.
  const cacheKey = options.wallCount
    ? `${category}_wallCount${options.wallCount}`
    : options.sinkType
      ? `${category}_sinkType${options.sinkType}`
      : options.subtype?.field
        ? `${category}_${options.subtype.field}${options.subtype.value}`
        : category;

  // Check cache first
  if (glbModelCache.has(cacheKey)) {
    return glbModelCache.get(cacheKey);
  }

  // Dedupe concurrent callers: share the same in-flight fetch
  if (glbModelInflight.has(cacheKey)) {
    return glbModelInflight.get(cacheKey);
  }

  const fetchPromise = (async () => {
    try {
      // Fetch GLB models from Firestore

      // Query Firestore for models of this category
      const modelsCollection = collection(db, 'unifiedObjects');

      // When category is 'sink' and a sinkType option is provided, filter by
      // the canonical (type='sink', sinkType=<mount>) shape.
      let querySnapshot;
      if (category === SINK_TYPE && options.sinkType) {
        querySnapshot = await getDocs(
          query(
            modelsCollection,
            where('type', '==', SINK_TYPE),
            where('sinkType', '==', options.sinkType)
          )
        );
      } else if (options.subtype?.field) {
        // Two equality filters — Firestore serves these by merging the two
        // single-field indexes, so no composite index is needed.
        querySnapshot = await getDocs(
          query(
            modelsCollection,
            where('type', '==', category),
            where(options.subtype.field, '==', options.subtype.value)
          )
        );
      } else {
        querySnapshot = await getDocs(
          query(modelsCollection, where('type', '==', category))
        );
      }

      let models = [];

      // A NARROWED query that comes back empty means "this type has none of
      // that subtype" — a real answer. Falling through to the legacy
      // `category` lookup would widen it back to every model of the type,
      // which is the whole thing the narrowing exists to prevent.
      if (querySnapshot.empty && !options.subtype?.field) {
        // Try with 'category' field if 'type' field doesn't match
        // Try with 'category' field
        const categoryQuery = query(
          modelsCollection,
          where('category', '==', category)
        );
        const categorySnapshot = await getDocs(categoryQuery);

        // Process models found by category field
        categorySnapshot.forEach((doc) => {
          models.push({
            ...doc.data(),
            id: doc.id,
          });
        });
      } else {
        // Process models found by type field
        querySnapshot.forEach((doc) => {
          models.push({
            ...doc.data(),
            id: doc.id,
          });
        });
      }

      // Filter out disabled objects
      models = models.filter((model) => model.disabled !== true);

      // Filter by wallCount for showers
      if (category === 'shower' && options.wallCount !== undefined) {
        models = models.filter(
          (model) => model.wallCount === options.wallCount
        );
        console.log(
          `🚿 Filtered shower models: ${models.length} models with wallCount=${options.wallCount}`
        );
      }

      // Cache the filtered results
      glbModelCache.set(cacheKey, models);

      return models;
    } catch (error) {
      console.error(`Error fetching GLB models for ${category}:`, error);
      return [];
    } finally {
      glbModelInflight.delete(cacheKey);
    }
  })();

  glbModelInflight.set(cacheKey, fetchPromise);
  return fetchPromise;
};

// Cache for the attributed-models list (rarely changes; fetched on demand by
// the credits surface).
let attributedModelsCache = null;

/**
 * Get the unifiedObjects models that legally REQUIRE an attribution credit —
 * i.e. CC-BY-licensed assets. CC0 assets need no attribution, so they're
 * excluded even when they carry a courtesy `attribution` string.
 *
 * Backs the in-app credits/acknowledgements surface. Returns a lightweight
 * projection (not the full model docs).
 *
 * @returns {Promise<Array<{id:string, name:string, attribution:string,
 *   license:string|null, author:string|null, sourceUrl:string|null}>>}
 */
export const getAttributedModels = async () => {
  if (!db) return [];
  if (attributedModelsCache) return attributedModelsCache;

  try {
    const modelsCollection = collection(db, 'unifiedObjects');
    const snapshot = await getDocs(
      query(modelsCollection, where('attribution', '!=', null))
    );
    const models = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (!data.attribution) return;
      // Only CC-BY family licenses require the credit be shown.
      if (!/CC-BY/i.test(data.license || '')) return;
      models.push({
        id: docSnap.id,
        name: data.name || docSnap.id,
        attribution: data.attribution,
        license: data.license || null,
        author: data.author || null,
        sourceUrl: data.sourceUrl || null,
      });
    });
    // Stable display order by name.
    models.sort((a, b) => a.name.localeCompare(b.name));
    attributedModelsCache = models;
    return models;
  } catch (error) {
    console.error('Error fetching attributed models:', error);
    return [];
  }
};

/**
 * Get a specific GLB model by ID and category
 * @param {string} category - The object category
 * @param {string} modelId - The model ID
 * @returns {Promise<Object|null>} The GLB model data or null
 */
// Resolved single models, keyed by `<category>|<modelId>`. A miss is remembered
// as `null` so a scan referencing a deleted model doesn't re-query every pass.
const modelByIdCache = new Map();
// Concurrent callers for the same model share one fetch. `ArchitecturalScene`
// resolves all ~109 objects through `Promise.all`, and a scan reuses the same
// handful of ids across them — undeduped, every object fired its own read.
const modelByIdInflight = new Map();

export const getGLBModelById = async (category, modelId) => {
  if (!category || !modelId) {
    return null;
  }

  const cacheKey = `${category}|${modelId}`;
  if (modelByIdCache.has(cacheKey)) {
    return modelByIdCache.get(cacheKey);
  }
  if (modelByIdInflight.has(cacheKey)) {
    return modelByIdInflight.get(cacheKey);
  }

  // Fetch the ONE document, not its whole type bucket.
  //
  // This used to be `getGLBModelsByCategory(category).find(m => m.id === modelId)`
  // — every wall-light in the catalog (77 docs / 1.67MB, 939KB of it a
  // `debugCircles` field nothing here reads) pulled down to pick one. It is on
  // the critical path twice over: `ArchitecturalScene` resolves every object's
  // metadata through it before the scene is usable, and `SceneObject` blocks
  // its GLB download on the same data.
  //
  // The bucket read stays as a FALLBACK, so no legacy catalog shape changes
  // behaviour: the fast path only answers when the document plainly has the
  // right `type`. Everything else — a doc that matches on `category` instead
  // of `type`, an id under the old `saved_` spelling, a missing doc — falls
  // through to the original path and resolves exactly as it did before.
  //
  // A DISABLED document still answers. Every caller resolves the model an
  // object already names (scene metadata, the resize check, the applied model
  // in the Design Center), and disabling retires a model from pickers, not
  // from the scans that placed it (`utils/catalogPickable`).
  const fetchPromise = (async () => {
    let resolved = null;
    try {
      const snap = await getDoc(doc(db, 'unifiedObjects', modelId));
      if (snap.exists()) {
        const model = { ...snap.data(), id: snap.id };
        if (model.type === category) {
          resolved = model;
        }
      }
    } catch (error) {
      // Non-fatal: fall through to the bucket read.
      console.error(`Error fetching GLB model ${modelId}:`, error);
    }

    if (!resolved) {
      const models = await getGLBModelsByCategory(category);
      resolved = models.find((m) => m.id === modelId) || null;
    }

    modelByIdCache.set(cacheKey, resolved);
    return resolved;
  })().finally(() => {
    modelByIdInflight.delete(cacheKey);
  });

  modelByIdInflight.set(cacheKey, fetchPromise);
  return fetchPromise;
};

// Cabinet-ish objects all draw from the same four buckets; named once so the
// two consumers below cannot disagree about what "storage" covers.
const STORAGE_CATALOG_TYPES = [
  'lower-cabinet',
  'upper-cabinet',
  'storage-furniture',
  'storage',
];

/**
 * The Firestore `type`s whose documents may back an object RENDERED as
 * `objectType`. A render type is not always one stored type: a `shower` can be
 * backed by a `corner-shower` record, a bathroom `sink` by a `vanity`, and
 * every cabinet flavour by any of four buckets.
 *
 * Order matters and matches the order the buckets used to be concatenated in,
 * because a lookup takes the first match.
 */
export const catalogTypesForRenderType = (objectType) => {
  switch (objectType) {
    case 'shower':
      return ['shower', 'corner-shower'];
    case 'showerhead':
      return ['showerhead', 'shower-stem'];
    case 'kitchen-sink':
      return ['kitchen-sink'];
    case SINK_TYPE:
      return [SINK_TYPE, 'vanity'];
    case 'storage':
    case 'cabinet':
    case 'lower-cabinet':
    case 'upper-cabinet':
    case 'storage-furniture':
      return STORAGE_CATALOG_TYPES;
    default:
      return [objectType];
  }
};

// One resolved document per model id, shared by every object using it.
// Keyed by id alone (not id+type) because it caches the DOCUMENT, and the
// per-render-type acceptance test is applied by the caller below.
const modelDocCache = new Map();

const fetchModelDoc = (modelId) => {
  if (!modelDocCache.has(modelId)) {
    const docPromise = getDoc(doc(db, 'unifiedObjects', modelId))
      .then((snap) => (snap.exists() ? { ...snap.data(), id: snap.id } : null))
      .catch((error) => {
        // Non-fatal: the caller falls back to the bucket read. Drop the
        // promise so a later mount retries rather than caching the failure.
        modelDocCache.delete(modelId);
        console.error(`Error fetching GLB model doc ${modelId}:`, error);
        return null;
      });
    modelDocCache.set(modelId, docPromise);
  }
  return modelDocCache.get(modelId);
};

/**
 * The catalog records needed to RENDER one object.
 *
 * `SceneObject` only ever uses the single record whose id is `modelId`
 * (`modelResolution.resolveModelForRender`), but it used to fetch every
 * document of every type in the object's catalog to find it — up to four
 * bucket queries per category, and the wall-light bucket alone is 77 docs /
 * 1.67MB, 939KB of it a `debugCircles` field nothing here reads. That ran
 * BEFORE the GLB url was known, so it sat directly in front of every model
 * download on the page. This is the same fix `getGLBModelById` already
 * carries, applied to the render path that still paid the old cost.
 *
 * The fast path fetches that one document and returns it alone. It only
 * answers when the document has a `type` (or legacy `category`) in this render
 * type's catalog. Everything else falls through to the original bucket fetch
 * and resolves exactly as before. That INCLUDES a genuine miss: the caller must
 * still receive a populated catalog that it fails to find the id in, because a
 * miss against a real catalog is what drives its `modelUrl` fallback.
 * Returning an empty list there would silently stop such an object rendering
 * at all.
 *
 * A DISABLED document still renders. The object already names it, and
 * disabling retires a model from pickers, not from the scans that placed it
 * (`utils/catalogPickable`). Before this, a disabled model dropped to the
 * `modelUrl` fallback, which carries no catalog `rotation`, lamp points or
 * mounting points — it loaded on its side, unlit.
 *
 * `showerhead` is deliberately never fast-pathed: `SceneObject` also picks a
 * default `shower-stem` out of this same list, so for that one render type the
 * whole (small) catalog IS the answer. The buckets hold only pickable models,
 * so a disabled showerhead the object names is added to them.
 */
export const getRenderCatalogForObject = async (objectType, modelId) => {
  if (!objectType || !modelId || !db) {
    return [];
  }

  const types = catalogTypesForRenderType(objectType);

  const model = await fetchModelDoc(modelId);
  const namedModel =
    model &&
    (types.includes(model.type) ||
      (typeof model.category === 'string' && types.includes(model.category)))
      ? model
      : null;
  if (namedModel && objectType !== 'showerhead') {
    return [namedModel];
  }

  const buckets = await Promise.all(
    types.map((type) => getGLBModelsByCategory(type))
  );
  const catalog = buckets.flat();
  return namedModel && !catalog.some((m) => m.id === namedModel.id)
    ? [...catalog, namedModel]
    : catalog;
};

/**
 * Get the best matching GLB model based on style preferences
 * @param {string} objectType - The object type (e.g., 'toilet', 'sink', 'bathtub')
 * @param {Object} stylePreferences - User's style preferences {style: [], color: [], materials: []}
 * @returns {Promise<Object|null>} The best matching GLB model or null if none found
 */
export const getBestMatchingModel = async (
  objectType,
  stylePreferences,
  options = {}
) => {
  if (!stylePreferences) {
    return getDefaultGLBModel(objectType, options);
  }

  try {
    console.log(
      `🎨 Finding best matching ${objectType} model for preferences:`,
      stylePreferences,
      options
    );

    const models = await getGLBModelsByCategory(objectType, options);

    if (models.length === 0) {
      console.log(`⚠️ No models found for ${objectType}`);
      return null;
    }

    // Score each model based on keyword match
    const scoredModels = models.map((model) => {
      let score = 0;
      let matchCount = 0;

      // Score style keywords
      if (stylePreferences.style && stylePreferences.style.length > 0) {
        stylePreferences.style.forEach((stylePref) => {
          const modelScore = model.keywords?.style?.[stylePref] || 0;
          score += modelScore;
          matchCount++;
        });
      }

      // Score color keywords
      if (stylePreferences.color && stylePreferences.color.length > 0) {
        stylePreferences.color.forEach((colorPref) => {
          const modelScore = model.keywords?.color?.[colorPref] || 0;
          score += modelScore;
          matchCount++;
        });
      }

      // Score material keywords
      if (stylePreferences.materials && stylePreferences.materials.length > 0) {
        stylePreferences.materials.forEach((materialPref) => {
          const modelScore = model.keywords?.materials?.[materialPref] || 0;
          score += modelScore;
          matchCount++;
        });
      }

      // Average score across all preferences
      const averageScore = matchCount > 0 ? score / matchCount : 0;

      return {
        model,
        score: averageScore,
        matchCount,
      };
    });

    // Sort by score descending
    scoredModels.sort((a, b) => b.score - a.score);

    const bestMatch = scoredModels[0];

    if (bestMatch) {
      if (bestMatch.score > 0) {
        console.log(
          `✅ Best matching ${objectType} model: ${bestMatch.model.name} (score: ${bestMatch.score.toFixed(2)})`
        );
      } else {
        console.log(
          `⚠️ No keyword matches for ${objectType}, using first available model: ${bestMatch.model.name}`
        );
      }
      return bestMatch.model;
    } else {
      // No models found at all, fall back to default
      console.log(`⚠️ No models found for ${objectType}, using default`);
      return getDefaultGLBModel(objectType);
    }
  } catch (error) {
    console.error(
      `Error finding best matching model for ${objectType}:`,
      error
    );
    return getDefaultGLBModel(objectType);
  }
};

/**
 * Get the default GLB model for a specific object type
 * @param {string} objectType - The object type (e.g., 'toilet', 'sink', 'bathtub')
 * @returns {Promise<Object|null>} The default GLB model or null if none set
 */
export const getDefaultGLBModel = async (objectType, options = {}) => {
  if (!objectType || !db) {
    return null;
  }

  try {
    // Fast path: no subtype scoping requested, so the shared default index
    // answers it without fetching the type's whole model list — and answers a
    // MISS as authoritatively as a hit. The index is every non-disabled
    // `isDefaultForType` document keyed by both of the fields the bucket
    // queries match on, so if neither map holds `objectType` there is no
    // document the bucket read could return that `pickDefaultForType` would
    // accept. Falling through would be two guaranteed-empty round trips.
    if (
      !options.tubType &&
      !options.sinkType &&
      options.wallCount === undefined
    ) {
      const { byType, byCategory } = await loadTypeDefaultIndex();
      const indexed = byType.get(objectType) || byCategory.get(objectType);
      if (indexed) return indexed;
      console.log(`⚠️ No default GLB model set for ${objectType}`);
      return null;
    }

    // Subtype-scoped lookup (tubType / sinkType / wallCount), or a caller that
    // passed a `category` rather than a `type` — both need the category-aware
    // list to pick from.
    const models = await getGLBModelsByCategory(objectType, options);

    // Find the model marked as default for this type
    const defaultModel = pickDefaultForType(models);

    if (defaultModel) {
      console.log(
        `✅ Found default GLB model for ${objectType}:`,
        defaultModel.name
      );
    } else {
      console.log(`⚠️ No default GLB model set for ${objectType}`);
    }

    return defaultModel || null;
  } catch (error) {
    console.error(`Error fetching default GLB model for ${objectType}:`, error);
    return null;
  }
};

/**
 * Set a GLB model as the default for its object type
 * This will unset any other default for the same type (and subtype if applicable)
 * @param {string} modelId - The model ID to set as default
 * @param {string} objectType - The object type
 * @param {Object} [options] - Optional filters
 * @param {string} [options.tubType] - For bathtubs, scope default to this tubType
 * @returns {Promise<boolean>} Success status
 */
export const setDefaultGLBModel = async (modelId, objectType, options = {}) => {
  if (!modelId || !objectType || !db) {
    return false;
  }

  try {
    const { tubType, sinkType } = options;
    const subtype = tubType ?? sinkType;
    const label = subtype ? `${objectType} (${subtype})` : objectType;
    console.log(`🔧 Setting model ${modelId} as default for type ${label}`);

    const batch = writeBatch(db);

    // First, get all models of this type
    const models = await getGLBModelsByCategory(objectType);

    // Unset existing defaults — scoped by subtype where the catalog keeps
    // one default PER subtype: tubType for bathtubs, sinkType for sinks.
    // Without the sinkType scope, setting a pedestal default silently
    // cleared the undermount/vessel/dropin defaults and every scan-built
    // vanity sink lost its model (rendered invisible).
    //
    // For sinks the scope applies even when the new default has NO
    // sinkType: a legacy untagged sink doc must only sweep other untagged
    // defaults, never the tagged subtype defaults — falling through to an
    // unscoped sweep would reproduce the exact clobbering this fixes.
    const isSinkType = objectType === 'sink';
    models.forEach((model) => {
      if (model.id !== modelId && model.isDefaultForType === true) {
        if (tubType && model.tubType !== tubType) return;
        if (isSinkType) {
          if ((model.sinkType ?? null) !== (sinkType ?? null)) return;
        } else if (sinkType && model.sinkType !== sinkType) {
          return;
        }
        const modelRef = doc(db, 'unifiedObjects', model.id);
        batch.update(modelRef, { isDefaultForType: false });
        console.log(`  Unsetting default for model ${model.id}`);
      }
    });

    // Set the new default
    const newDefaultRef = doc(db, 'unifiedObjects', modelId);
    batch.update(newDefaultRef, { isDefaultForType: true });

    await batch.commit();

    // Clear cache to force refresh
    clearGLBModelCache();

    console.log(`✅ Successfully set model ${modelId} as default for ${label}`);
    return true;
  } catch (error) {
    console.error(`Error setting default GLB model:`, error);
    return false;
  }
};

/**
 * Unset a GLB model as default for its type
 * @param {string} modelId - The model ID to unset
 * @returns {Promise<boolean>} Success status
 */
export const unsetDefaultGLBModel = async (modelId) => {
  if (!modelId || !db) {
    return false;
  }

  try {
    console.log(`🔧 Unsetting model ${modelId} as default`);

    const modelRef = doc(db, 'unifiedObjects', modelId);
    await updateDoc(modelRef, { isDefaultForType: false });

    // Clear cache to force refresh
    clearGLBModelCache();

    console.log(`✅ Successfully unset model ${modelId} as default`);
    return true;
  } catch (error) {
    console.error(`Error unsetting default GLB model:`, error);
    return false;
  }
};

/**
 * Update a fixture's designStyles array in Firestore
 * @param {string} fixtureId - The unifiedObjects document ID
 * @param {Array<string>} designStyles - Array of style strings (e.g., ['contemporary', 'modern'])
 */
export const updateFixtureDesignStyles = async (fixtureId, designStyles) => {
  const docRef = doc(db, 'unifiedObjects', fixtureId);
  await updateDoc(docRef, { designStyles });
  clearGLBModelCache();
};

/**
 * Clear the GLB model cache
 * Useful when models are updated in Firestore
 */
export const clearGLBModelCache = () => {
  glbModelCache.clear();
  modelByIdCache.clear();
  modelDocCache.clear();
  typeDefaultIndexPromise = null;
  // The type-default registry is derived from these models — invalidate it too
  // so a freshly-set/unset default is re-resolved on the next render pass.
  clearTypeDefaultRegistry();
};

/**
 * Preload GLB models for multiple categories
 * @param {Array<string>} categories - Array of categories to preload
 */
export const preloadGLBModels = async (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return;
  }

  // Preload GLB models for specified categories

  const promises = categories.map((category) =>
    getGLBModelsByCategory(category)
  );
  await Promise.all(promises);

  // GLB models preloaded successfully
};
